import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import pca, { authRequest } from '../../app/msal';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: 'https://graph.microsoft.com/v1.0',
  prepareHeaders: async (headers) => {
    const authSettings = {
      ...authRequest,
      account: pca.getAllAccounts()[0] as AccountInfo,
    };
    const getTokens = async (settings: any) => {
      return pca.acquireTokenSilent(settings).catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return pca.acquireTokenRedirect(settings);
        }
      });
    };
    // Request tokens from Azure AD (if needed)
    let response = await getTokens(authSettings);
    // If token will expire in less than 5 minutes, force a refresh of the token.
    if (
      Math.floor(Number((response as any)?.expiresOn) / 1000) <
      Math.floor(Date.now() / 1000) + 60 * 5
    ) {
      response = await getTokens({ ...authSettings, forceRefresh: true });
    }

    headers.set('Authorization', `Bearer ${(response as any)?.accessToken}`);

    return headers;
  },
});

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Provide a dynamic base query that attaches the correct base url.
  // This is necessary for easily integration testing with the backend because the REACT_APP_API_BASE_URL is dynamic and changes at the start of every test.
  const urlEnd = typeof args === 'string' ? args : args.url;
  // construct a dynamically generated portion of the url
  const adjustedUrl = `https://graph.microsoft.com/v1.0/${urlEnd}`;
  const adjustedArgs =
    typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  // provide the amended url and other params to the raw base query
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

let keepUnusedDataFor = 60;
/* istanbul ignore if */
if (process.env.NODE_ENV === 'test') {
  // Do not cache data for tests
  keepUnusedDataFor = 0;
}
// Define a service using a base URL and expected endpoints
export const microsoftAPI = createApi({
  reducerPath: 'microsoftAPI',
  baseQuery: dynamicBaseQuery,
  keepUnusedDataFor,
  endpoints: (builder) => ({}),
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
//export const { useGetUserQuery } = sepAPI;
